import API from '@aws-amplify/api';
import i18n from '../../i18n';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AcceptDecline from './acceptDecline/index.js';
import { applicationStages, appTypes } from '../../utils/enums';
import Button from '../shared/Button';
import ChangeSchool from './ChangeSchool';
import CreateNew from './application/createNewModal';
import Delete from './application/Delete';
//Removed with SHIP-1470
// import FileDrop from "../shared/FileDrop";
import DateTimeText from '../shared/DateTimeText';
import Panel from '../shared/Panel';
import { ProtectedRouteContext } from '../../components/Context';
import Status from '../shared/Status';
import WithdrawApp from './WithdrawApp';
import WithdrawProgram from './WithdrawProgram';

import './ApplicationsParent.scss';

function ApplicationsParent() {
    const history = useHistory();
    const [t] = useTranslation();
    const [gettingApplicationList, setGettingApplicationList] = useState(true);
    const [availableApplicationsList, setAvailableApplicationsList] = useState([]);
    const [existingApplicationsList, setExistingApplicationsList] = useState([]);
    const [existingShipyardAppInstanceCount, setExistingShipyardAppInstanceCount] = useState(0);
    const { displayModal, userSettings } = useContext(ProtectedRouteContext);
    const [lang, setLang] = useState(
        typeof userSettings?.LanguagePreference === 'string' ? userSettings.LanguagePreference : i18n.language
    );
    const [dismissed, setDismissed] = useState(new Set([]));

    const launchNewApplicationModal = async () => {
        if (existingApplicationsList && existingApplicationsList.length === 0) {
            startApplication();
            return;
        }

        let applicationDefinitionId = availableApplicationsList[0].id;

        let closeCreateModal = () => {
            displayModal(false);
        };
        let createModal = (
            <CreateNew
                applicationDefinitionId={applicationDefinitionId}
                close={closeCreateModal}
            />
        );
        displayModal(true, createModal);
    };

    const startApplication = async (studentId) => {
        //NOTE: Phase 1 only allows one active application definition per client. Future
        //  phases must make provisions to appropriately list all available applications
        //  and allow the user to pick which application they want to begin.
        setGettingApplicationList(true);
        let chosenApplicationId = availableApplicationsList[0].id;

        let body = {
            applicationId: chosenApplicationId,
        };

        if (!!studentId) body.studentId = parseInt(studentId);

        try {
            let startNewResponse = await API.post('ShipyardAPI', '/application', {
                body,
            });

            if (!startNewResponse.applicationInstanceId) {
                toast.error(t('Applications.Create.Error'));
                console.error('An error occurred while creating a new application instance.');
                return;
            }

            history.push('/application/' + startNewResponse.applicationInstanceId);
        } catch (e) {
            toast.error(t('General.AjaxError'));
        }

        setGettingApplicationList(false);
    };

    const getApplicationsList = async () => {
        setGettingApplicationList(true);
        let response = await API.get('ShipyardAPI', '/applications');

        for (let i = 0; i < response.applicationInstances.length; i++) {
            const appInst = response.applicationInstances[i];
            appInst.actions = [];

            let _viewAction = {
                value: 'view',
                labelKey:
                    applicationStages[appInst?.Status?.id] === 'AppStages.Started' ||
                    applicationStages[appInst?.Status?.id] === 'AppStages.Returned'
                        ? 'Applications.List.Actions.Edit'
                        : 'Applications.List.Actions.View',
            };
            let _acceptDeclineAction = {
                value: 'accept-decline',
                labelKey:
                    applicationStages[appInst?.Status?.id] === 'AppStages.Pending'
                        ? 'Applications.List.Actions.AcceptDecline'
                        : 'Applications.List.Actions.ViewScholarship',
            };
            let _withdrawApplicationAction = {
                value: 'withdraw-app',
                labelKey: 'Applications.List.Actions.WithdrawApplication',
            };
            // TODO - Add this back when we get the flow right.
            // SHIP-1224
            // let _deleteApplicationAction = {
            //     value: "delete",
            //     labelKey: "Applications.List.Actions.Delete"
            // };

            switch (applicationStages[appInst?.Status?.id]) {
                case 'AppStages.Started':
                    // TODO - Add this back when we get the flow right.
                    // SHIP-1224 (_deleteApplicationAction)
                    appInst.actions.push(_viewAction);
                    break;
                case 'AppStages.SDEReview':
                case 'AppStages.Returned':
                case 'AppStages.Approved':
                    appInst.actions.push(_viewAction, _withdrawApplicationAction);
                    break;
                case 'AppStages.Pending':
                    appInst.actions.push(_viewAction, _acceptDeclineAction, _withdrawApplicationAction);
                    break;
                case 'AppStages.Accepted':
                    appInst.actions.push(_viewAction, _acceptDeclineAction);
                    break;
                default:
                    appInst.actions.push(_viewAction);
                    break;
            }
        }

        // TODO SHIP-920 - Move this filter to the backend
        const RemoveSignatureAttachments = (applicationInstance) => {
            applicationInstance.Attachments = applicationInstance.Attachments.filter(
                (attachment) => attachment.DocumentTypeID !== 7 && attachment.DocumentTypeID !== 9
            );
            return applicationInstance;
        };

        let _applicationInstances = response.applicationInstances.map(RemoveSignatureAttachments);

        setAvailableApplicationsList(response.availableApplications);
        setExistingApplicationsList(_applicationInstances);
        setExistingShipyardAppInstanceCount(
            response.applicationInstances.filter((a) => {
                return !a.IsLegacy;
            }).length
        );
        setGettingApplicationList(false);
    };

    const messageJSX = useMemo(() => {
        const dismissMessage = (ID) => {
            setDismissed((prev) => {
                prev.add(ID);

                return new Set(prev);
            });
        };

        if (typeof existingApplicationsList?.map === 'function') {
            return existingApplicationsList.map((app) => {
                return app?.Status?.id === 3 ? (
                    <div
                        key={`app-${app.ID}`}
                        className="review message"
                    >
                        <FontAwesomeIcon icon={faExclamationCircle} />{' '}
                        {t('Applications.ReviewMessage', { ApplicantName: !!app?.StudentName ? app.StudentName : 'No Name' })}
                    </div>
                ) : app?.Status?.id === 6 && !dismissed.has(app.ID) ? (
                    <div
                        key={`app-${app.ID}`}
                        className="approved message"
                    >
                        <FontAwesomeIcon icon={faExclamationCircle} />{' '}
                        {t('Applications.ApprovedMessage', { ApplicantName: !!app?.StudentName ? app.StudentName : 'No Name' })}{' '}
                        <FontAwesomeIcon
                            className="close-icon"
                            icon={faTimesCircle}
                            onClick={() => {
                                dismissMessage(app.ID);
                            }}
                        />
                    </div>
                ) : app?.Status?.id === 8 ? (
                    <div
                        key={`app-${app.ID}`}
                        className="pending message"
                    >
                        <FontAwesomeIcon icon={faExclamationCircle} />{' '}
                        {t('Applications.PendingMessage', { ApplicantName: !!app?.StudentName ? app.StudentName : 'No Name' })}
                    </div>
                ) : null;
            });
        }
    }, [existingApplicationsList, dismissed, t]);

    const actionsHandler = (selectedAction, selectedAppInstId) => {
        switch (selectedAction.value) {
            case 'accept-decline':
                let closeAcceptDecline = (action) => {
                    displayModal(false);
                    if (action) {
                        getApplicationsList();
                    }
                };

                let selapp = existingApplicationsList.find((app) => app.ID === selectedAppInstId);

                displayModal(
                    true,
                    <AcceptDecline
                        StudentName={selapp.StudentName}
                        AppInstID={selapp.ID}
                        SchoolName={selapp.SchoolName}
                        Status={selapp?.Status?.id}
                        handleClose={closeAcceptDecline}
                    />
                );
                break;

            case 'change-pvt-school':
                let changeSchoolModal = <ChangeSchool close={() => displayModal(false)} />;

                displayModal(true, changeSchoolModal);
                break;

            case 'view':
                history.push('/application/' + selectedAppInstId);
                break;

            case 'delete':
                let app = existingApplicationsList.find((app) => app.ID === selectedAppInstId);
                let closeDeleteModal = (deleted) => {
                    displayModal(false);
                    if (!deleted) return;
                    getApplicationsList();
                };
                let deleteModal = (
                    <Delete
                        applicationInstance={app}
                        close={closeDeleteModal}
                    />
                );
                displayModal(true, deleteModal);
                break;

            case 'withdraw-program':
                let withdrawProgramModal = (
                    <WithdrawProgram
                        close={() => displayModal(false)}
                        app={existingApplicationsList.find((app) => app.ID === selectedAppInstId)}
                    />
                );

                displayModal(true, withdrawProgramModal);
                break;

            case 'withdraw-app':
                let closeWithdrawApp = (action) => {
                    displayModal(false);
                    if (action) getApplicationsList();
                };

                let withdrawAppModal = (
                    <WithdrawApp
                        close={closeWithdrawApp}
                        app={existingApplicationsList.find((app) => app.ID === selectedAppInstId)}
                    />
                );

                displayModal(true, withdrawAppModal);
                break;

            default:
                console.error(`Action '${selectedAction.value}' not implemented.`);
                break;
        }
    };

    useEffect(() => {
        (async () => {
            await getApplicationsList();
        })();
    }, []);

    useEffect(() => {
        if (typeof userSettings?.LanguagePreference === 'string') {
            setLang(userSettings.LanguagePreference);
        }
    }, [userSettings.LanguagePreference]);

    const applicationsPlaceholder = (
        <Panel>
            <div className="app-instance placeholder">
                <div className="student-name shimmer" />
                <div className="term shimmer" />
                <div className="details">
                    <div className="detail">
                        <div className="label shimmer" />
                        <div className="data shimmer" />
                    </div>
                    <div className="detail">
                        <div className="label shimmer" />
                        <div className="data shimmer" />
                    </div>
                    <div className="detail">
                        <div className="label shimmer" />
                        <div className="data shimmer" />
                    </div>
                    <div className="detail">
                        <div className="label shimmer" />
                        <div className="data shimmer" />
                    </div>
                    <div className="detail">
                        <div className="label shimmer" />
                        <div className="data shimmer" />
                    </div>
                </div>
                <div className="attachments-container">
                    <div className="label shimmer" />
                    <div className="attachment-list">
                        <div className="attachment">
                            <div className="icon shimmer" />
                            <div className="detail shimmer" />
                        </div>
                        <div className="attachment">
                            <div className="icon shimmer" />
                            <div className="detail shimmer" />
                        </div>
                        <div className="attachment">
                            <div className="icon shimmer" />
                            <div className="detail shimmer" />
                        </div>
                    </div>
                </div>
            </div>
        </Panel>
    );

    return (
        <div
            id="applications-parent-component"
            className={gettingApplicationList ? 'loading' : ''}
        >
            <div className="header">
                <h1>
                    <ReactMarkdown>{t('Applications.Header')}</ReactMarkdown>
                </h1>
                <Button
                    defaultText={t('Applications.Create.Button')}
                    onClick={launchNewApplicationModal}
                    disabled={gettingApplicationList}
                    interactive={false}
                />
            </div>

            {messageJSX?.length > 0 ? messageJSX : null}

            {gettingApplicationList ? (
                applicationsPlaceholder
            ) : (
                <Panel>
                    {existingShipyardAppInstanceCount < 1 && (
                        <div className="no-app-instances">
                            <ReactMarkdown children={availableApplicationsList[0].headerMarkdown[lang]} />
                        </div>
                    )}

                    {existingApplicationsList.map((app) => (
                        <div
                            className="app-instance"
                            key={app.ID}
                        >
                            <div className="student-name">
                                {!!app.StudentName && !!app.StudentName.trim() ? app.StudentName : t('Applications.NoName')}
                            </div>
                            <div className="term">{app.Term}</div>
                            <div className="details">
                                <div className="detail">
                                    <div className="label">{t('Applications.List.Date')}</div>
                                    <div className="data">
                                        <DateTimeText
                                            date={app.StartDate}
                                            type="long"
                                        />
                                    </div>
                                </div>
                                <div className="detail">
                                    <div className="label">{t('Applications.List.Status')}</div>
                                    <div className="data">
                                        <Status status={app.Status} />
                                    </div>
                                </div>
                                <div className="detail">
                                    <div className="label">{t('Applications.List.Amount')}</div>
                                    <div className="data">{app.ScholarshipAmount ? `$${app.ScholarshipAmount.toFixed(2)}` : '- -'}</div>
                                </div>
                                <div className="detail">
                                    <div className="label">{t('Applications.List.School')}</div>
                                    <div className="data">{app.SchoolName}</div>
                                </div>
                                <div className="detail">
                                    <div className="label">{t('Applications.List.Enrollment')}</div>
                                    <div className="data">{app.EnrollmentStatus}</div>
                                </div>
                                <div className="detail">
                                    <div className="label">{t('Applications.List.ApplicationType')}</div>
                                    <div className="data">{t(appTypes[app.ApplicationType])}</div>
                                </div>
                            </div>
                            {/* TODO: #TechDebut - This was removed as part of SHIP-1470 and may need to be added back if a better solution is found. */}
                            {/* <div className="attachments-container">
                                <div className="label">{t("Applications.List.Attachments")}</div>
                                <div className="attachment-list">
                                    {app.Attachments.length > 0 ?
                                        app.Attachments.map((attachment) => {
                                            let fileDate = new Date(attachment.LastUpdatedUTC);
                                            let _files = [
                                                {
                                                    name: attachment.FileName,
                                                    fileId: attachment.FileID,
                                                    s3id: attachment.S3ID,
                                                    uploadedOn: fileDate,
                                                    type: "pdf",
                                                },
                                            ];
                                            return <FileDrop key={`attachment-${attachment.FileID}`} files={_files} labelText={attachment.DocumentType} downloadOnly={true} />;
                                        })
                                    :
                                        <p>{t("Applications.NoDocs")}</p>
                                    }
                                </div>
                            </div> */}
                            <div className="actions-container">
                                <Select
                                    classNamePrefix="action-select"
                                    isSearchable={false}
                                    menuPlacement={'auto'}
                                    options={app.actions}
                                    onChange={(selectedOption) => actionsHandler(selectedOption, app.ID)}
                                    placeholder={<span>{t('Applications.List.Actions.Placeholder')}</span>}
                                    value={null}
                                    formatOptionLabel={(option) => (
                                        <div className="action-select__option">
                                            <span>{t(option.labelKey)}</span>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    ))}
                </Panel>
            )}
        </div>
    );
}

export default ApplicationsParent;
